// contact-session.js — subbie / client session storage.
//
// v0.43.0 — initial. A subbie's session payload lives under
//           'droppin.contact.v1' in localStorage, DISTINCT from the
//           admin/owner session at 'droppin.user.v1' (see user.js).
//
// Keeping the two keys separate means an admin can also be invited as a
// contact (different tenant or different role) without one session
// clobbering the other. AppShell branches on which key is present.
//
// v0.56.0 — the InSpec CLIENT-PORTAL hand-off (api/portal-handoff) also
//           mints a droppin_contact session, but the person is a CLIENT
//           (their portal identity), not a no-account subcontractor. The
//           hand-off response carries route:'/projects' for a client
//           (view:'projects' in the signed token) vs the default
//           '/my-items' for a subbie. We persist that distinction so:
//             • AppShell keeps a CLIENT in the admin shell on /projects
//               (the Locations page) instead of bouncing them to My items;
//             • a genuine SUBBIE (no client marker) is still bounced to
//               /my-items exactly as before.
//           The marker is derived from the hand-off route and mirrored to
//           an explicit `kind` so a future non-route caller can set it too.
//
// Shape:
//   {
//     contact: { id, tenantId, name, email, phone, company, trade },
//     projects: [{ id, name, address }],
//     stats: { totalAssigned, openAssigned },
//     route: '/projects' | '/my-items',   // hand-off destination (v0.56.0)
//     kind:  'client' | 'subbie',          // derived landing kind (v0.56.0)
//     loggedInAt: ISO timestamp
//   }

const STORAGE_KEY = 'droppin.contact.v1';
// v0.336.0 — which visit made the contact session (sessionStorage: this tab /
// this run of the app only). See endStaleContactSession.
const VISIT_KEY = 'droppin.contact.visit';

export function getContactSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

export function setContactSession(payload) {
  try {
    // v0.56.0 — derive the landing kind so consumers don't each re-implement
    // the rule. A CLIENT hand-off carries route:'/projects'; anything else
    // (subbie magic-link, or a hand-off with no client view) is a subbie.
    const route = payload && typeof payload.route === 'string' ? payload.route : null;
    const kind = payload && payload.kind
      ? payload.kind
      : (route === '/projects' ? 'client' : 'subbie');
    const enriched = { ...payload, route: route || undefined, kind, loggedInAt: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(enriched));
    try { sessionStorage.setItem(VISIT_KEY, enriched.loggedInAt); } catch (_e) { /* no sessionStorage */ }
  } catch (_e) { /* ignore */ }
}

export function clearContactSession() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
  try { sessionStorage.removeItem(VISIT_KEY); } catch (_e) { /* ignore */ }
}

// v0.336.0 — AN OFFICE SIGN-IN IS NEVER LEFT LIVING AS A CLIENT. Brett's
// iPhone, the evening he tested client links and the client home: "Phone
// doesn't have live or paused." A client (or subbie) contact session lives in
// localStorage until someone signs out, so one made by a hand-off on the
// office's own phone outlived the visit it was made for, and every office
// screen after it treated Brett as that client — no Live, no Pause, the
// Locations list read-only — with nothing on screen to say why.
//
// The rule: on a device that also holds an office sign-in (droppin.user.v1), a
// contact session lasts for the visit that made it (this tab / this run of
// the installed app). Opened again later, it is ended — its cookie cleared
// server-side, its identity forgotten — and the office sign-in carries on.
// While it lasts the shell says so, with a way back (AppShell's bar).
// A device with no office sign-in — a real client's or subbie's own phone —
// keeps its contact session exactly as before.
//
//   endStaleContactSession({ officeUser, visit, logout }) → true when it ended one
export function endStaleContactSession({
  officeUser = null,
  visit = () => { try { return sessionStorage.getItem(VISIT_KEY); } catch (_e) { return null; } },
  logout = () => { try { fetch('/api/contact-logout', { method: 'POST', credentials: 'same-origin' }).catch(() => {}); } catch (_e) { /* best-effort */ } },
} = {}) {
  const s = getContactSession();
  if (!s || !officeUser) return false;
  const here = visit();
  if (here && s.loggedInAt && here === s.loggedInAt) return false;   // made in this visit
  clearContactSession();
  logout();
  return true;
}

// v0.336.0 — this device is in a contact view made over an office sign-in
// (the shell names it and offers the way back).
export function contactViewOverOffice(officeUser) {
  return !!officeUser && hasContactSession();
}

export function hasContactSession() {
  return !!getContactSession();
}

// v0.56.0 — true when the current contact session is a CLIENT (InSpec
// client-portal hand-off asking for the Locations view), false for a
// no-account subcontractor. Used by AppShell (don't bounce a client to
// /my-items) and ProjectsScreen (render the read-only client Locations
// list from the contact-cookie endpoint).
export function isClientContactSession() {
  const s = getContactSession();
  return !!s && (s.kind === 'client' || s.route === '/projects');
}

// v0.56.0 — true only for a genuine subcontractor session (a contact
// session that is NOT a client). AppShell bounces exactly these to
// /my-items.
export function isSubbieContactSession() {
  const s = getContactSession();
  return !!s && !(s.kind === 'client' || s.route === '/projects');
}
